import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { Box } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import { calculateDifferences } from 'src/components/dashboard/DashboardPage/components/ComparisonPill/helper';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import BaseResponseRateWidget from 'src/components/dashboard/DashboardPage/widgets/ResponseRateWidget/BaseResponseRateWidget';
import type { Aggregate } from 'src/components/followUp/common/types';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

const getAggregates = (data: Aggregate | undefined) => ({
  totalValue: data?.totalObjects,
  relativeValue: getRelativeValue(data?.value, data?.totalObjects, 0),
  absoluteValue: data?.value,
});

const ResponseRateWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.RESPONSE_RATE,
    config.id,
  );

  const { data, isFetching, refetch, errorMsg, comparisonData } =
    useWidgetQuery<Aggregate>(endpoint);

  const aggregate = useMemo(() => getAggregates(data), [data]);

  const comparisonValues = useMemo(() => {
    if (!comparisonData) return undefined;

    const comparisonAggregate = getAggregates(comparisonData);

    return calculateDifferences(aggregate, comparisonAggregate);
  }, [aggregate, comparisonData]);

  return (
    <Box sx={{ py: 2, px: 3, width: '100%' }}>
      <BaseResponseRateWidget
        aggregate={aggregate}
        comparisonValues={comparisonValues}
        loading={isFetching}
        errorMsg={errorMsg}
        refetch={refetch}
        ariaId={ariaId}
        name={config.name}
      />
    </Box>
  );
};

export default ResponseRateWidget;
